import {
    checkAtlassianCredentials,
    getAtlassianInfo,
    importTicketsStream,
    initiateAtlassianOAuth,
    listJiraProjects,
    selectJiraProject,
    verifyAtlassianDomain
} from "@/api/integrations/atlassian";
import { openOAuthPopup } from "./oauthPopup";

export type FlowStage =
    | "idle" | "authorizing" | "verifying" | "selecting_project"
    | "ready_to_sync" | "importing" | "synced";

export interface JiraAccount { email: string | null; domain: string; cloudId: string }
export interface JiraProject { id: string; key: string; name: string }

export interface JiraConnectFlowState {
    stage: FlowStage;
    savedChecked: boolean;
    savedConnection: JiraAccount | null;
    account: JiraAccount | null;
    pickerMode: "saved" | "new";
    domainInput: string;
    projects: JiraProject[];
    selectedProject: JiraProject | null;
    syncProgress: { imported: number; total: number } | null;
    syncedImported: number | null;
    error: { stage: FlowStage; message: string } | null;
}

function initialState(): JiraConnectFlowState {
    return {
        stage: "idle",
        savedChecked: false,
        savedConnection: null,
        account: null,
        pickerMode: "new",
        domainInput: "",
        projects: [],
        selectedProject: null,
        syncProgress: null,
        syncedImported: null,
        error: null
    };
}

let state = initialState();
const listeners = new Set<() => void>();
let tokenGetter: (() => Promise<string>) | null = null;
let contextSink: ((note: Record<string, unknown>) => void) | null = null;
// Bumped on every reset/switch/change so in-flight async work becomes stale.
let epoch = 0;
// Set by retry() when a verifying-stage error is retried; if the retry also
// fails, runVerify sends the flow back to the authorizing picker instead of
// erroring in place (spec §7 Card 2).
let verifyRetried = false;
// AbortController for the in-flight SSE import, if any — aborted on
// switchAccount/changeProject/reset so an abandoned import stops streaming.
let importAbort: AbortController | null = null;

function setState(patch: Partial<JiraConnectFlowState>) {
    state = { ...state, ...patch };
    listeners.forEach(l => l());
}

export function getJiraConnectState(): JiraConnectFlowState {
    return state;
}

export function subscribeJiraConnectFlow(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function configureJiraConnectFlow(getToken: () => Promise<string>) {
    tokenGetter = getToken;
}

export function setJiraConnectContextSink(sink: ((note: Record<string, unknown>) => void) | null) {
    contextSink = sink;
}

export function resetJiraConnectFlow() {
    epoch += 1;
    importAbort?.abort();
    importAbort = null;
    verifyRetried = false;
    state = initialState();
    listeners.forEach(l => l());
}

function pushNote(note: Record<string, unknown>) {
    contextSink?.(note);
}

async function token(): Promise<string> {
    if (!tokenGetter) throw new Error("Jira connect flow is not configured");
    return tokenGetter();
}

function fail(stage: FlowStage, message: string) {
    setState({ error: { stage, message } });
    pushNote({
        jira_connect: { event: "error", stage, message },
        note: `Jira connect hit a problem during ${stage}: ${message} The user can retry from the card.`
    });
}

// A retried verify that fails again means the linked credential is no longer
// usable — send the flow back to the authorizing picker rather than looping
// on verify forever (spec §7 Card 2).
function failVerifyToAuthorizing(message: string) {
    pushNote({
        jira_connect: { event: "error", stage: "verifying", message },
        note: `Jira connect hit a problem during verifying: ${message} Re-authorization is needed — the user can reconnect from the card.`
    });
    verifyRetried = false;
    setState({
        stage: "authorizing",
        account: null,
        pickerMode: state.savedConnection ? "saved" : "new",
        error: null
    });
}

// ── Stage runners ─────────────────────────────────────────────────────────

async function runCheckSaved() {
    const myEpoch = epoch;
    try {
        // Backend resolves the user from auth claims; the userId arg is vestigial.
        const res = await checkAtlassianCredentials(0, await token());
        if (myEpoch !== epoch) return;
        if (res.hasCredentials && res.cloudId && res.domain) {
            setState({
                savedChecked: true,
                pickerMode: "saved",
                savedConnection: { email: res.email ?? null, domain: res.domain, cloudId: res.cloudId }
            });
        } else {
            setState({ savedChecked: true, pickerMode: "new", savedConnection: null });
        }
    } catch {
        // Treat as no saved connection — OAuth still works.
        if (myEpoch === epoch) setState({ savedChecked: true, pickerMode: "new", savedConnection: null });
    }
}

function advanceAuthorized(account: JiraAccount) {
    setState({ account, savedConnection: account, error: null });
    pushNote({
        jira_connect: { event: "authorized", email: account.email, domain: account.domain },
        note: `Jira authorized as ${account.email ?? account.domain}.`
    });
    enterVerifying();
}

async function runOAuth() {
    const myEpoch = epoch;
    const domain = state.domainInput.trim();
    if (!domain) {
        fail("authorizing", "Enter your Atlassian site domain first.");
        return;
    }
    try {
        const verified = await verifyAtlassianDomain(domain, await token());
        if (myEpoch !== epoch) return;
        if (!verified.success || !verified.cloudId) {
            fail("authorizing", verified.error || "That Atlassian domain could not be verified.");
            return;
        }
        // Backend resolves the user from auth claims; userId is vestigial.
        const oauth = await initiateAtlassianOAuth(
            { userId: 0, domain, cloudId: verified.cloudId }, await token()
        );
        if (myEpoch !== epoch) return;
        const authUrl = oauth.authUrl ?? oauth.authorizationUrl;
        if (!authUrl) {
            fail("authorizing", "The authorization URL could not be created.");
            return;
        }
        const result = await openOAuthPopup(authUrl);
        if (myEpoch !== epoch) return;
        if (result === "retry") {
            void runOAuth();
            return;
        }
        if (result === "cancel") {
            fail("authorizing", "Authorization was not completed.");
            return;
        }
        // "success" — and "closed", which may still have succeeded (popup can
        // close before its postMessage lands). Re-check credentials to decide.
        const check = await checkAtlassianCredentials(0, await token());
        if (myEpoch !== epoch) return;
        // The re-checked credential must match the cloudId verified earlier in
        // this run — otherwise a closed, un-authorized popup for a different
        // account would silently advance the flow as the OLD saved account.
        if (check.hasCredentials && check.cloudId && check.domain && check.cloudId === verified.cloudId) {
            advanceAuthorized({ email: check.email ?? null, domain: check.domain, cloudId: check.cloudId });
        } else {
            fail("authorizing", "Authorization was not completed.");
        }
    } catch (e) {
        if (myEpoch === epoch) fail("authorizing", e instanceof Error ? e.message : "Authorization failed.");
    }
}

function enterVerifying() {
    setState({ stage: "verifying", error: null });
    void runVerify();
}

async function runVerify() {
    const myEpoch = epoch;
    try {
        const info = await getAtlassianInfo({ userId: 0 }, await token());
        if (myEpoch !== epoch) return;
        if (info.status === "LINKED") {
            verifyRetried = false;
            pushNote({ jira_connect: { event: "verified" }, note: "Jira connection verified." });
            enterSelectingProject();
        } else if (verifyRetried) {
            failVerifyToAuthorizing("Connection could not be verified.");
        } else {
            fail("verifying", "Connection could not be verified.");
        }
    } catch (e) {
        if (myEpoch === epoch) {
            const message = e instanceof Error ? e.message : "Connection could not be verified.";
            if (verifyRetried) {
                failVerifyToAuthorizing(message);
            } else {
                fail("verifying", message);
            }
        }
    }
}

function enterSelectingProject() {
    setState({ stage: "selecting_project", error: null, syncProgress: null, syncedImported: null });
    void runLoadProjects();
}

async function runLoadProjects() {
    const myEpoch = epoch;
    const account = state.account;
    if (!account) return;
    try {
        const res = await listJiraProjects(account.cloudId, await token());
        if (myEpoch !== epoch) return;
        setState({ projects: res.projects });
    } catch (e) {
        if (myEpoch === epoch) fail("selecting_project", e instanceof Error ? e.message : "Couldn't load projects.");
    }
}

async function runConfirmProject() {
    const myEpoch = epoch;
    const account = state.account;
    const project = state.selectedProject;
    if (!account || !project) return;
    try {
        await selectJiraProject(
            { cloudId: account.cloudId, projectId: project.id, projectName: project.name }, await token()
        );
        if (myEpoch !== epoch) return;
        pushNote({
            jira_connect: { event: "project_linked", projectKey: project.key, projectName: project.name },
            note: `Jira project ${project.key} (${project.name}) linked. Ticket import is starting automatically.`
        });
        setState({ stage: "ready_to_sync", error: null });
        enterImporting();
    } catch (e) {
        if (myEpoch === epoch) fail("selecting_project", e instanceof Error ? e.message : "Couldn't link the project.");
    }
}

function enterImporting() {
    setState({ stage: "importing", error: null, syncProgress: { imported: 0, total: 0 } });
    void runImport();
}

async function runImport() {
    const myEpoch = epoch;
    const account = state.account;
    const project = state.selectedProject;
    if (!account || !project) return;
    const controller = new AbortController();
    importAbort = controller;
    try {
        const result = await importTicketsStream(
            { cloudId: account.cloudId, projectId: project.id, projectName: project.name, projectKey: project.key },
            await token(),
            p => {
                if (myEpoch === epoch && p.total > 0) {
                    setState({ syncProgress: { imported: p.current, total: p.total } });
                }
            },
            controller.signal
        );
        if (myEpoch !== epoch) return;
        setState({ stage: "synced", syncedImported: result.imported });
        pushNote({
            jira_connect: { event: "sync_complete", imported: result.imported },
            note: `Jira sync complete. ${result.imported} tickets imported into the Context Mesh. The connection is now fully established.`
        });
    } catch (e) {
        if (myEpoch === epoch) fail("importing", e instanceof Error ? e.message : "Import failed.");
    } finally {
        if (importAbort === controller) importAbort = null;
    }
}

// ── Public actions (all stage-guarded) ────────────────────────────────────

export const jiraConnectActions = {
    beginAuthorize() {
        // A completed flow or one stuck in an error must be restartable —
        // otherwise the agent's "wait for a note" contract hangs forever.
        if (state.stage === "synced" || state.error !== null) resetJiraConnectFlow();
        if (state.stage !== "idle") return;
        setState({ stage: "authorizing" });
        void runCheckSaved();
    },
    useSavedAccount() {
        if (state.stage !== "authorizing" || !state.savedConnection) return;
        advanceAuthorized(state.savedConnection);
    },
    connectAnother() {
        if (state.stage !== "authorizing") return;
        setState({ pickerMode: "new", error: null });
    },
    setDomainInput(value: string) {
        setState({ domainInput: value });
    },
    startOAuth() {
        if (state.stage !== "authorizing") return;
        setState({ error: null });
        void runOAuth();
    },
    chooseProject(project: JiraProject) {
        if (state.stage !== "selecting_project") return;
        setState({ selectedProject: project });
    },
    confirmProject() {
        if (state.stage !== "selecting_project" || !state.selectedProject) return;
        void runConfirmProject();
    },
    switchAccount() {
        if (state.stage === "idle") return;
        epoch += 1;
        importAbort?.abort();
        importAbort = null;
        verifyRetried = false;
        setState({
            stage: "authorizing",
            account: null,
            pickerMode: state.savedConnection ? "saved" : "new",
            projects: [],
            selectedProject: null,
            syncProgress: null,
            syncedImported: null,
            error: null
        });
    },
    changeProject() {
        if (!state.account) return;
        epoch += 1;
        importAbort?.abort();
        importAbort = null;
        enterSelectingProject();
    },
    retry() {
        const errored = state.error;
        if (!errored) return;
        setState({ error: null });
        switch (errored.stage) {
            case "authorizing":
                void runOAuth();
                break;
            case "verifying":
                verifyRetried = true;
                enterVerifying();
                break;
            case "selecting_project":
                void runLoadProjects();
                break;
            case "importing":
                enterImporting();
                break;
            default:
                break;
        }
    }
};
